import { AxiosResponse } from 'axios';
import { config } from 'config/config.local';
import {
  IGetReportsParams,
  IGetReportsResponse,
  IGetReportsScheduleResponse,
  INewReportParams,
  IPopularReport,
  IPopularReportCreationInfo,
  IReport,
  IReportCreator,
  IReportDL,
  IReportPreferencesResponse,
  IReportSchedule,
  IReportSchedulePayload,
} from 'contexts/reporting/types';
import { BUSINESS_PYTHON } from './axios';

export const getReports = (
  params: IGetReportsParams
): Promise<AxiosResponse<IGetReportsResponse>> => {
  return BUSINESS_PYTHON.get(config.routes.reporting.reports, { params });
};

export const newReport = ({
  type,
  datesFilter,
  activeFilters,
  uaa_ids,
  team_ids,
}: INewReportParams): Promise<AxiosResponse<IReport>> => {
  const paramsParser = {
    type,
    from_date: datesFilter.from,
    to_date: datesFilter.to,
    columns: activeFilters,
    uaa_ids: uaa_ids,
    team_ids: team_ids,
  };
  return BUSINESS_PYTHON.post(`${config.routes.reporting.reports}`, { ...paramsParser });
};

export const downloadReport = (id: string): Promise<AxiosResponse<IReportDL>> => {
  return BUSINESS_PYTHON.get(`${config.routes.reporting.reportsWithId}/${id}/download/`);
};

export const downloadReportGroup = (id: string): Promise<AxiosResponse<IReportDL>> => {
  return BUSINESS_PYTHON.get(`${config.routes.reporting.reportsGroup}/${id}/download/`);
};

export const getReportPreferences = (
  reportType?: string
): Promise<AxiosResponse<{ results: IReportPreferencesResponse[] }>> => {
  const queryParams = reportType ? `/?type=${reportType}` : '';
  return BUSINESS_PYTHON.get(`${config.routes.reporting.checkboxes}${queryParams}`);
};

export const saveReportPreferences = (
  checkboxes: string[],
  reportType: string
): Promise<AxiosResponse<IReportPreferencesResponse>> => {
  const payload = {
    checkboxes,
    type: reportType,
  };
  return BUSINESS_PYTHON.post(`${config.routes.reporting.checkboxes}`, { ...payload });
};

export const getReportsSchedule = (params: {
  ordering?: string;
  frequency?: string;
  page?: number;
  page_size?: number;
  search?: string;
}): Promise<AxiosResponse<IGetReportsScheduleResponse>> =>
  BUSINESS_PYTHON.get(config.routes.reporting.reportsSchedule, { params });

export const patchReportsSchedule = (
  id: string,
  payload: Partial<IReportSchedule>
): Promise<AxiosResponse<IGetReportsScheduleResponse>> =>
  BUSINESS_PYTHON.patch(`${config.routes.reporting.reportsSchedule}/${id}/`, { ...payload });

export const postReportsSchedule = (
  payload: IReportSchedulePayload
): Promise<AxiosResponse<IGetReportsScheduleResponse>> =>
  BUSINESS_PYTHON.post(`${config.routes.reporting.reportsSchedule}/`, { ...payload });

export const deleteReportsSchedule = (
  id: string
): Promise<AxiosResponse<IGetReportsScheduleResponse>> =>
  BUSINESS_PYTHON.delete(`${config.routes.reporting.reportsSchedule}/${id}/`);

export const patchReport = (
  id: string,
  payload: Partial<IReportSchedule>
): Promise<AxiosResponse<IReport>> =>
  BUSINESS_PYTHON.patch(`${config.routes.reporting.reports}${id}/`, { ...payload });

export const deleteReport = (id: string): Promise<AxiosResponse<IReport>> =>
  BUSINESS_PYTHON.delete(`${config.routes.reporting.reports}${id}/`);

export const getReportCreators = (): Promise<AxiosResponse<IReportCreator[]>> => {
  return BUSINESS_PYTHON.get(`${config.routes.reporting.reportCreators}`);
};

export const getPopularReports = (): Promise<AxiosResponse<{ results: IPopularReport[] }>> => {
  return BUSINESS_PYTHON.get(`${config.routes.reporting.popularReports}`);
};

export const generatePopularReport = (params: {
  popular_report_id: string;
  from_date: string;
  to_date: string;
  uaa_ids?: string[];
  team_ids?: string[];
}): Promise<AxiosResponse<IPopularReportCreationInfo>> => {
  const url = config.routes.reporting.generatePopularReport(params.popular_report_id);
  return BUSINESS_PYTHON.post(url, { ...params });
};

export const schedulePopularReport = (frequency: string[], reportId: string): Promise<void> => {
  const url = config.routes.reporting.schedulePopularReport(reportId);
  return BUSINESS_PYTHON.post(url, { frequency });
};
